import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { Button, FormControl, MenuItem, Select } from '@mui/material';
import FormInput from 'app/shared-components/formComponents/FormInput';
import FormSelect from 'app/shared-components/formComponents/FormSelect';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import { clsx } from 'clsx';
import { FilterField, FilterFieldType, filterIconByType, FiltersFormValues } from './FiltersForm';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { endOfDay, startOfDay } from 'date-fns';
import DateRangeRow from './DateRangeRow';

interface IProps {
  options: FilterField[];
  index: number;
  field: {
    mode?: string;
    isGroup: boolean;
    rules: {
      mode: string;
      field: string;
      value?: string | number | null | undefined;
    }[];
  };
  removeRule: () => void;
  removable?: boolean;
}

const modesByType: Record<FilterFieldType, { value: string; label: string; nullable?: boolean }[]> =
  {
    number: [
      { value: 'equals', label: '=', nullable: false },
      { value: 'not', label: '!=', nullable: false },
      { value: 'lt', label: '<', nullable: false },
      { value: 'lte', label: '<=', nullable: false },
      { value: 'gt', label: '>', nullable: false },
      { value: 'gte', label: '>=', nullable: false },
      { value: 'equals:null', label: 'is empty', nullable: true },
      { value: 'not:null', label: 'is not empty', nullable: true },
    ],
    string: [
      { value: 'equals', label: '=', nullable: false },
      { value: 'not', label: '!=', nullable: false },
      { value: 'contains', label: 'contains', nullable: false },
      { value: 'startsWith', label: 'starts with', nullable: false },
      { value: 'endsWith', label: 'ends with', nullable: false },
      { value: 'equals:null', label: 'is empty', nullable: true },
      { value: 'not:null', label: 'is not empty', nullable: true },
    ],
    date: [],
    select: [],
  };

export type StringFilterType =
  | 'equals'
  | 'not'
  | 'contains'
  | 'startsWith'
  | 'endsWith'
  | 'equals:null'
  | 'not:null';
export type NumberFilterType =
  | 'equals'
  | 'not'
  | 'lt'
  | 'lte'
  | 'gt'
  | 'gte'
  | 'equals:null'
  | 'not:null';

function dateToRange(date: Date) {
  const start = startOfDay(date);
  const end = endOfDay(date);
  return { gte: start.toJSON(), lte: end.toJSON() };
}

const FiltersGroup = ({ index, options, field, removeRule, removable }: IProps) => {
  const {
    getValues,
    watch,
    control,
    setValue,
    clearErrors,
    formState: { errors },
  } = useFormContext<FiltersFormValues>();

  const {
    fields: rules,
    append,
    remove,
  } = useFieldArray<FiltersFormValues>({
    name: `filters.${index}.rules`,
  });

  return (
    <div className="flex w-full gap-16 h-full mb-24">
      <div className="flex flex-col w-1/6 justify-between">
        {index === 0 && <div className=" h-full mb-8 ">Where</div>}
        {index !== 0 && !field.isGroup && (
          <FormControl>
            <Controller
              name={`filters.${index}.mode`}
              control={control}
              render={({ field: { onChange, value } }) => (
                <Select
                  onChange={(e) => {
                    if (index === 1) setValue('filters.0.mode', e.target.value as 'AND' | 'OR');

                    onChange(e);
                  }}
                  value={value}
                >
                  <MenuItem value="AND">And</MenuItem>
                  <MenuItem value="OR">Or</MenuItem>
                </Select>
              )}
            />
          </FormControl>
        )}

        {field.isGroup && <div className=" h-full mb-8 ">And</div>}
      </div>

      <div
        className={clsx(
          'flex flex-col gap-8 w-full',
          field.isGroup && 'bg-gray-100 border-2 border-gray-200 rounded-8 p-12',
        )}
      >
        {rules.map((rule, ruleIndex) => {
          const getOption = () =>
            options.find(
              (option) => option.key === watch(`filters.${index}.rules.${ruleIndex}.field`),
            );
          const getShowMode = () => ['string', 'number'].includes(getOption()?.type || '');
          return (
            <div className="flex items-start gap-16 w-full" key={rule.id}>
              <FormSelect
                id={`filters.${index}.rules.${ruleIndex}.field`}
                label=""
                control={control}
                onChange={(value) => {
                  setTimeout(() => {
                    const option = getOption();
                    const type = option?.type;
                    if (!type) return;
                    if (option.defaultMode)
                      setValue(`filters.${index}.rules.${ruleIndex}.mode`, option.defaultMode);

                    setValue(`filters.${index}.rules.${ruleIndex}.type`, type);
                    if (type === 'date') {
                      setValue(
                        `filters.${index}.rules.${ruleIndex}.value`,
                        dateToRange(new Date()) as any,
                      );
                    } else {
                      setValue(`filters.${index}.rules.${ruleIndex}.value`, null as any);
                    }
                  }, 5);
                }}
                className={clsx('bg-white', getOption()?.range ? ' w-2/3 ' : 'w-full ')}
              >
                {options.map((opt) => (
                  <MenuItem key={opt.key} value={opt.key}>
                    <div className="flex items-center gap-6 ml-[-5px]">
                      <FuseSvgIcon>{filterIconByType[opt.type]}</FuseSvgIcon>
                      {opt.label}
                    </div>
                  </MenuItem>
                ))}
              </FormSelect>
              {getShowMode() && (
                <FormSelect
                  id={`filters.${index}.rules.${ruleIndex}.mode`}
                  control={control}
                  className="w-full bg-white "
                >
                  {!!getOption()?.type &&
                    modesByType[getOption()!.type]
                      ?.filter((op) => getOption()?.nullable || !op.nullable)
                      .map((opts) => (
                        <MenuItem key={opts.value} value={opts.value}>
                          {opts.label}
                        </MenuItem>
                      ))}
                </FormSelect>
              )}

              {getOption()?.type !== 'date' ? (
                getOption()?.options?.length ? (
                  <FormSelect
                    control={control}
                    id={`filters.${index}.rules.${ruleIndex}.value`}
                    error={!!errors.filters?.[index]?.rules?.[ruleIndex]?.value}
                    errorMessage={errors.filters?.[index]?.rules?.[ruleIndex]?.value?.message}
                    className="w-full bg-white "
                  >
                    {getOption()?.options?.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </FormSelect>
                ) : (
                  <FormInput
                    control={control}
                    id={`filters.${index}.rules.${ruleIndex}.value`}
                    error={!!errors.filters?.[index]?.rules?.[ruleIndex]?.value}
                    errorMessage={errors.filters?.[index]?.rules?.[ruleIndex]?.value?.message}
                    className="w-full bg-white "
                    disabled={
                      watch(`filters.${index}.rules.${ruleIndex}.mode`) === 'equals:null' ||
                      watch(`filters.${index}.rules.${ruleIndex}.mode`) === 'not:null'
                    }
                  />
                )
              ) : getOption()?.range ? (
                <DateRangeRow
                  values={watch(`filters.${index}.rules.${ruleIndex}.value`) as any}
                  onChange={(from, to) => {
                    if (from && !to) {
                      return setValue(
                        `filters.${index}.rules.${ruleIndex}.value`,
                        {
                          gte: startOfDay(from).toJSON(),
                        } as any,
                        { shouldValidate: true },
                      );
                    }
                    if (!from && to) {
                      return setValue(
                        `filters.${index}.rules.${ruleIndex}.value`,
                        {
                          lte: endOfDay(to).toJSON(),
                        } as any,
                        { shouldValidate: true },
                      );
                    }
                    if (from && to) {
                      return setValue(
                        `filters.${index}.rules.${ruleIndex}.value`,
                        {
                          gte: startOfDay(from).toJSON(),
                          lte: endOfDay(to).toJSON(),
                        } as any,
                        { shouldValidate: true },
                      );
                    }
                  }}
                />
              ) : (
                <Controller
                  render={({ field }) => (
                    <DatePicker
                      className="w-full"
                      {...field}
                      value={
                        (field.value as any)?.lte ? new Date((field.value as any).lte) : new Date()
                      }
                      onChange={(v) => {
                        if (!v) return;
                        return field.onChange(dateToRange(v));
                      }}
                      maxDate={new Date()}
                      minDate={new Date(Date.UTC(1900, 0, 1))}
                    />
                  )}
                  name={`filters.${index}.rules.${ruleIndex}.value`}
                  control={control}
                  disabled={
                    watch(`filters.${index}.rules.${ruleIndex}.mode`) === 'equals:null' ||
                    watch(`filters.${index}.rules.${ruleIndex}.mode`) === 'not:null'
                  }
                />
              )}
              {removable && (
                <Button
                  size="small"
                  onClick={() => {
                    if (getValues('filters').length === 1) return;

                    if (field.isGroup && rules.length > 1) return remove(ruleIndex);

                    removeRule();
                  }}
                >
                  <FuseSvgIcon>material-solid:close</FuseSvgIcon>
                </Button>
              )}
            </div>
          );
        })}
        {field.isGroup && (
          <Button
            size="small"
            variant="contained"
            className=" w-fit font-400 "
            onClick={() => {
              append({
                field: options[0].key,
                mode: options[0].defaultMode ?? '',
                value: '',
                type: options[0].type,
              });
            }}
          >
            <FuseSvgIcon className="mr-4">material-outline:add</FuseSvgIcon> Add
          </Button>
        )}
      </div>
    </div>
  );
};

export default FiltersGroup;
